import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Monitor, CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

const API = `${BACKEND_URL}/api`;

export default function DeviceRegistrationPage() {
  const { token } = useParams();
  const [linkInfo, setLinkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [registering, setRegistering] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Auto-detect device name from user agent
    const ua = navigator.userAgent;
    if (/Mobile|Android|iPhone|iPad/.test(ua)) {
      if (/iPhone/.test(ua)) setDeviceName("iPhone");
      else if (/iPad/.test(ua)) setDeviceName("iPad");
      else if (/Android/.test(ua)) setDeviceName("Android Device");
      else setDeviceName("Mobile Device");
    } else if (/Windows/.test(ua)) {
      setDeviceName("Windows Laptop");
    } else if (/Macintosh/.test(ua)) {
      setDeviceName("MacBook");
    } else if (/Linux/.test(ua)) {
      setDeviceName("Linux PC");
    } else {
      setDeviceName("My Device");
    }

    fetchLinkInfo();
  }, [token]);

  const fetchLinkInfo = async () => {
    try {
      const res = await axios.get(`${API}/devices/register-info/${token}`);
      setLinkInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired registration link");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    setError("");
    try {
      // Generate or use existing device token
      let deviceToken = localStorage.getItem("device_token");
      if (!deviceToken) {
        deviceToken = "dev_" + crypto.randomUUID().replace(/-/g, "");
        localStorage.setItem("device_token", deviceToken);
      }

      const res = await axios.post(`${API}/devices/register/${token}`, {
        device_token: deviceToken,
        device_name: deviceName || "My Device",
      });

      if (res.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="device-registration-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            {success ? (
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            ) : error && !linkInfo ? (
              <XCircle className="w-8 h-8 text-red-500" />
            ) : (
              <Monitor className="w-8 h-8 text-primary" />
            )}
          </div>
          <h1 className="font-heading text-3xl tracking-tight">
            {success ? "Device Registered!" : "Register This Device"}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Pacfo Order Tracking System
          </p>
        </div>

        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="pt-6 space-y-4">
            {error && !linkInfo && (
              <div className="p-4 text-center">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-sm text-red-500 font-medium">{error}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Please contact your Super Admin for a new registration link.
                </p>
              </div>
            )}

            {success && (
              <div className="p-4 text-center space-y-3">
                <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto" />
                <p className="text-sm font-medium text-emerald-600">
                  This device has been successfully registered!
                </p>
                <p className="text-xs text-muted-foreground">
                  You can now log in to POTS from this device with full access.
                </p>
                <Button
                  className="w-full rounded-sm mt-4"
                  onClick={() => window.location.href = "/login"}
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
              </div>
            )}

            {linkInfo && !success && (
              <>
                <div className="p-3 rounded-sm bg-muted/50 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Registering for</span>
                    <span className="font-medium">{linkInfo.user_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="font-mono text-xs">{linkInfo.user_email}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deviceName">Device Name</Label>
                  <Input
                    id="deviceName"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="e.g., My Work Laptop"
                    className="rounded-sm"
                    data-testid="device-name-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Give this device a name to identify it later
                  </p>
                </div>

                {error && (
                  <div className="p-3 text-sm text-rose-500 bg-rose-50 rounded-sm border border-rose-200">
                    {error}
                  </div>
                )}

                <Button
                  className="w-full h-11 rounded-sm"
                  onClick={handleRegister}
                  disabled={registering}
                  data-testid="register-device-btn"
                >
                  {registering ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registering...</>
                  ) : (
                    <><Monitor className="w-4 h-4 mr-2" /> Register This Device</>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
